import {
  Box,
  Typography,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import { advisoryLayerExecutable } from "../../../../services/webServices";
import LinearProgressBar from "../../../common-components/QMentisLinearProgressBar";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  WidthType,
  TableRow,
  TableCell,
  Table,
  TableOfContents,
} from "docx";
import SuccessfulPage from "./SuccessfulPage";
import { MessagePopupContext } from "../../../main-pages/ServicePage";
import SessionExpired from "../../../popup-components/SessionExpiredPopup";

export const Top5QEPillars = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { topQEpillars, pillarTestSuggestions, finalCSV } =
    location.state || {};

  const [selectedTests, setSelectedTests] = useState({});
  const [updatedPillarSuggestions, setUpdatedPillarSuggestions] = useState({});
  const [loader, setLoader] = useState(false);
  const [documentBlob, setDocumentBlob] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [sessionExpiredValue, setSessionExpiredValue] = useState(false);

  const { setMessagePopup, setMessage } = useContext(MessagePopupContext);


  // Build sections from advisoryLayerExecutable JSON
  const buildSections = (content) => {
    if (!content?.sections) return [];
    const children = [];

    let currentSectionNum = null;
    let hasAddedSectionHeader = false;

    // Sort section keys to ensure proper order (0.1, 0.2, 1.1, 1.2, etc.)
    const sortedKeys = Object.keys(content.sections).sort((a, b) => {
      const [aMajor, aMinor] = a.split(".").map(Number);
      const [bMajor, bMinor] = b.split(".").map(Number);
      if (aMajor !== bMajor) return aMajor - bMajor;
      return aMinor - bMinor;
    });

    const sectionTitles = {
      0: "Executive CXO Summary",
      1: "Enterprise Archetype & Strategic Context",
      2: "Requirements--Architecture--Quality Alignment",
      3: "Architecture-Aware Quality Mapping",
      4: "Business Value Objectives & QE Value Flow",
      5: "Confidence Engineering Framework",
      6: "Non-Functional Quality & Runtime Resilience",
      7: "Quality Risk Intelligence & Resilience Strategy",
      8: "QE Operating Model & Roles",
      9: "QE Technology Stack & Platform Integration",
      10: "Metrics, Dashboards & Executive Reporting",
      11: "Roadmap, Rollout & Strategic Outcomes",
      12: "Appendix, Tools, Glossary & Credits",
    };

    sortedKeys.forEach((key) => {
      const section = content.sections[key];
      const sectionMatch = key.match(/^(\d+)\./);
      const sectionNum = sectionMatch ? sectionMatch[1] : null;

      // Add section header when entering a new major section
      if (sectionNum !== currentSectionNum) {
        currentSectionNum = sectionNum;
        hasAddedSectionHeader = false;
      }

      if (!hasAddedSectionHeader && sectionNum !== null) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Section ${sectionNum}: ${
                  sectionTitles[sectionNum] || "Unknown Section"
                }`,
                bold: true,
                size: 32,
                font: "Calibri",
                color: "000000",
              }),
            ],
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 300 },
          })
        );
        hasAddedSectionHeader = true;
      }

      const isExecutiveSection = key.startsWith("0.");

      // Add subsection title
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${key} ${section.title}`,
              bold: true,
              size: isExecutiveSection ? 28 : 26,
              font: "Calibri",
              color: isExecutiveSection ? "0066CC" : "000000",
            }),
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 },
        })
      );

      // Add summary paragraphs
      if (section.summary) {
        const summaryParagraphs = section.summary
          .split(/\n\s*\n/)
          .filter((para) => para.trim().length > 0);

        summaryParagraphs.forEach((para) => {
          const trimmedPara = para.trim();

          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: trimmedPara,
                  size: 22,
                  font: "Calibri",
                }),
              ],
              spacing: { before: 100, after: 200 },
              alignment: AlignmentType.JUSTIFIED,
            })
          );
        });
      }

      // Add bullets if present
      if (
        section.bullets &&
        Array.isArray(section.bullets) &&
        section.bullets.length > 0
      ) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Key Initiatives:",
                bold: true,
                size: 24,
                font: "Calibri",
              }),
            ],
            spacing: { before: 200, after: 100 },
          })
        );

        section.bullets.forEach((bullet) => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: bullet,
                  size: 22,
                  font: "Calibri",
                }),
              ],
              bullet: {
                level: 0,
              },
              spacing: { before: 100, after: 100 },
            })
          );
        });
      }

      // Add tables if present
      if (
        section.tables &&
        Array.isArray(section.tables) &&
        section.tables.length > 0
      ) {
        section.tables.forEach((tableData) => {
          if (tableData.title) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: tableData.title,
                    bold: true,
                    size: 24,
                    font: "Calibri",
                  }),
                ],
                spacing: { before: 300, after: 150 },
              })
            );
          }

          const tableRows = [];

          // Header row
          if (tableData.columns && tableData.columns.length > 0) {
            tableRows.push(
              new TableRow({
                children: tableData.columns.map(
                  (col) =>
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: col,
                              bold: true,
                              size: 22,
                              font: "Calibri",
                            }),
                          ],
                        }),
                      ],
                      shading: {
                        fill: "D9E2F3",
                      },
                    })
                ),
              })
            );
          }

          // Data rows
          if (tableData.rows && Array.isArray(tableData.rows)) {
            tableData.rows.forEach((row) => {
              tableRows.push(
                new TableRow({
                  children: row.map(
                    (cell) =>
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: String(cell || ""),
                                size: 22,
                                font: "Calibri",
                              }),
                            ],
                          }),
                        ],
                      })
                  ),
                })
              );
            });
          }

          // Add table to document
          children.push(
            new Table({
              rows: tableRows,
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
            })
          );

          // Add spacing after table
          children.push(
            new Paragraph({
              children: [new TextRun({ text: "" })],
              spacing: { before: 200, after: 200 },
            })
          );
        });
      }
    });

    return children;
  };

  useEffect(() => {
    if (pillarTestSuggestions) {
      const initialSelections = {};
      const initialSuggestions = {};

      Object.keys(pillarTestSuggestions).forEach((pillarName) => {
        const pillarData = pillarTestSuggestions[pillarName];
        initialSelections[pillarName] = {};

        pillarData.test_types.forEach((testType) => {
          initialSelections[pillarName][testType] = false;
        });

        initialSuggestions[pillarName] = {
          ...pillarData,
          test_types: [],
          test_count: 0,
        };
      });

      setSelectedTests(initialSelections);
      setUpdatedPillarSuggestions(initialSuggestions);
    }
  }, [pillarTestSuggestions]);

  const handleTestSelection = (pillarName, testType, isChecked) => {
    setSelectedTests((prev) => ({
      ...prev,
      [pillarName]: {
        ...prev[pillarName],
        [testType]: isChecked,
      },
    }));

    setUpdatedPillarSuggestions((prev) => {
      const newSuggestions = { ...prev };
      const originalPillar = pillarTestSuggestions[pillarName];

      if (originalPillar) {
        const selectedTestTypes = Object.entries({
          ...selectedTests[pillarName],
          [testType]: isChecked,
        })
          .filter(([_, selected]) => selected)
          .map(([testName, _]) => testName);

        newSuggestions[pillarName] = {
          ...originalPillar,
          test_types: selectedTestTypes,
          test_count: selectedTestTypes.length,
        };
      }

      return newSuggestions;
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleProceed = async () => {
    setLoader(true);

    if (!finalCSV) {
      alert("No CSV data found.");
      setLoader(false);
      return;
    }

    const parseCSVLine = (line) => {
      const result = [];
      let current = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') inQuotes = !inQuotes;
        else if (char === "," && !inQuotes) {
          result.push(current);
          current = "";
        } else current += char;
      }
      result.push(current);
      return result;
    };

    const rows = finalCSV.split("\n").filter((row) => row.trim());
    const headers = parseCSVLine(rows[0]);
    const dataRow = parseCSVLine(rows[1]);

    const newDataRow = [...dataRow];

    Object.keys(updatedPillarSuggestions).forEach((pillarName) => {
      const pillarIndex = headers.findIndex(
        (header) => header.trim().replace(/^"|"$/g, "") === pillarName
      );

      if (pillarIndex !== -1) {
        const selectedCount = getSelectedTestCount(pillarName);

        const tests =
          selectedCount === 0
            ? pillarTestSuggestions[pillarName]?.test_types || []
            : updatedPillarSuggestions[pillarName].test_types || [];

        newDataRow[pillarIndex] = tests.length > 0 ? tests.join("; ") : "";
      }
    });

    const escapeCSVField = (field) => {
      const fieldStr = String(field || "");
      if (
        fieldStr.includes(",") ||
        fieldStr.includes('"') ||
        fieldStr.includes("\n") ||
        fieldStr.includes(";")
      ) {
        return `"${fieldStr.replace(/"/g, '""')}"`;
      }
      return fieldStr;
    };

    const updatedCSVContent = [
      headers.map(escapeCSVField).join(","),
      newDataRow.map(escapeCSVField).join(","),
    ].join("\n");

    const csvBlob = new Blob(["\ufeff", updatedCSVContent], {
      type: "text/csv;charset=utf-8;",
    });

    const finalFormData = new FormData();
    finalFormData.append("file", csvBlob, "qe_strategy_report_final.csv");

    try {
      const finalResponse = await advisoryLayerExecutable(finalFormData);

      const content = finalResponse.data;

      // Build document sections
      const sections = buildSections(content);

      // Create Cover Page
      const coverPage = [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 600 },
          children: [
            new TextRun({
              text: "QE Strategy — Enterprise Report",
              bold: true,
              size: 48,
              font: "Calibri",
              color: "0A1F44",
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { before: 100, after: 100 },
          children: [
            new TextRun({
              text: `Generated: ${new Date()
                .toISOString()
                .slice(0, 16)
                .replace("T", " ")} UTC`,
              size: 24,
              font: "Calibri",
            }),
          ],
        }),
      ];

      const tocPage = [
        new Paragraph({
          text: "Table of Contents",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        }),

        new TableOfContents(" ", {
          hyperlink: true,
          headingStyleRange: "1-5",
        }),
      ];

      const doc = new Document({
        styles: {
          default: {
            document: {
              run: { font: "Calibri", size: 22 },
            },
          },
        },

        // ⭐⭐ THIS enables automatic TOC update (real page numbers)
        features: {
          updateFields: true,
        },

        sections: [
          {
            properties: { titlePage: true },
            children: coverPage,
          },
          {
            children: tocPage,
          },
          {
            properties: {
              page: {
                margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
              },
            },
            children: sections,
          },
        ],
      });

      // Generate and download Word doc
      const wordBlob = await Packer.toBlob(doc);
      setDocumentBlob(wordBlob);

      setLoader(false);
      setOpenDrawer(true);

      // Download CSV as well
      const csvLink = document.createElement("a");
      csvLink.href = URL.createObjectURL(csvBlob);
      csvLink.download = "qe_strategy_report_final.csv";
      csvLink.style.visibility = "hidden";
      document.body.appendChild(csvLink);
      csvLink.click();
      document.body.removeChild(csvLink);
      URL.revokeObjectURL(csvLink.href);
    } catch (error) {
      console.error("Full error:", error);
      console.error("Error response:", error.response);
      setLoader(false);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Unknown error occurred";

      setMessage(errorMessage);
      setMessagePopup(true);

      if (errorMessage === "Your session has expired.") {
        setSessionExpiredValue(true);
      }
    }
  };

  const getSelectedTestCount = (pillarName) => {
    if (!selectedTests[pillarName]) return 0;
    return Object.values(selectedTests[pillarName]).filter(Boolean).length;
  };

  if (!topQEpillars || !pillarTestSuggestions) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="error">
          No QE Pillars data available. Please go back and try again.
        </Typography>
        <Button variant="outlined" onClick={handleBack} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box p={3} maxWidth="100%" mx="auto">
        <Typography
          variant="h6"
          mb={2}
          sx={{
            fontWeight: 500,
            fontSize: "20px",
            fontStyle: "SemiBold",
            fontWeight: 600,
          }}
        >
          Human in the loop Suggested QE Pillars
        </Typography>

        <Grid container spacing={3}>
          {topQEpillars.map((pillarData, index) => {
            const pillarName = pillarData.pillar;
            const pillarSuggestion = pillarTestSuggestions[pillarName];

            if (!pillarSuggestion) return null;

            const testTypes = pillarSuggestion.test_types;

            return (
              <Grid
                item
                xs={12}
                sm={6}
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                }}
              >
                <Paper
                  sx={{
                    padding: 3,
                    borderRadius: 2,
                    boxShadow: 2,
                    height: "100%",
                  }}
                >
                  <Box sx={{ marginBottom: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        marginBottom: 1,
                        fontSize: "20px",
                        fontStyle: "SemiBold",
                      }}
                    >
                      {pillarName}
                    </Typography>
                  </Box>

                  <Grid container sx={{ color: "#3f3939de" }}>
                    {testTypes.map((testType, idx) => (
                      <Grid item xs={12} key={idx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                selectedTests[pillarName]?.[testType] || false
                              }
                              onChange={(e) =>
                                handleTestSelection(
                                  pillarName,
                                  testType,
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label={testType}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "16px",
                            },
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Box
        mt={4}
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#f6f3f2",
          padding: 1.2,
          paddingLeft: 3,
          paddingRight: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Button
          variant="outlined"
          sx={{
            minWidth: 110,
            textTransform: "none",
            fontSize: "16px",
            border: "2px solid #0094FB",
            borderRadius: "8px",
            gap: "8px",
          }}
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>

        <Box display="flex" flexDirection="column" alignItems="center">
          <Button
            variant="contained"
            sx={{
              minWidth: 110,
              fontSize: "16px",
              backgroundColor: "#0094FB",
              textTransform: "none",
              border: "2px solid #0094FB",
              borderRadius: "8px",
              gap: "8px",
              marginLeft: "150px",
            }}
            endIcon={<ArrowForwardIcon />}
            onClick={handleProceed}
          >
            Proceed
          </Button>
        </Box>
      </Box>

      {loader && <LinearProgressBar label={"Analyzing..."} />}

      {sessionExpiredValue && <SessionExpired />}

      <SuccessfulPage
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        documentBlob={documentBlob}
      />
    </Box>
  );
};
